import saveAs from 'file-saver';
import React, { useMemo } from 'react';
import BeaconChartWithLegend, {
  BeaconChartWithLegendProps
} from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import NoDataPlaceHolder from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';
import { SplineChartV2, SplineChartV2Series } from 'src/components/BeaconV2Components/SplineChartV2';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import { getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import convertSplineChartV2SeriesToDelimitedData from 'src/components/Charts/GenericChart/SeriesConverters/splineChartV2';
import { useChartMetricByWeekId } from 'src/serverProxy/useChartMetricByWeekId';
import { useDorisMetricByWeekId } from 'src/serverProxy/useDorisMetricByWeekId';
import { formatMetric } from 'src/utils/app';
import { calculateDerivedMetricByWeekId } from 'src/utils/chartUtils';
import { timestampToWeekId } from 'src/utils/dateUtils';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { useAppSelector, useWeekRange, WeekRange } from 'src/utils/Hooks/typedHooks';
import { DORIS_API_METRIC_TYPE } from 'src/utils/constants';
import {
  DorisMetricEndPointEnum,
  DorisMetricTypeEnum
} from 'src/components/AtlasV2Components/Consts/DorisFieldNameEnum';

/**
 * Converts [timestamp, value][] format to SplineChartV2Series format
 */
const convertToV2Series = (
  data: [number, number][],
  id: string,
  name: string
): SplineChartV2Series => ({
  id,
  name,
  data: (data || []).map(([timestamp, value]) => ({
    weekId: timestampToWeekId(timestamp),
    value
  }))
});

const transformUnitsSoldData = (rows) => rows?.map((row) => [row.weekId, row.value]) || [];

const transformTrafficData = (records) => records?.map((row) => [row.weekId, row.value]) || [];

// This will be used in keyMetricCard, so we took out the logic into hooks.
export const useConversionV2ChartData = () => {
  const weekRange = useWeekRange();

  const { response: dorisUnitsSoldResponse, isLoading: loadingDorisUnitsSold } = useDorisMetricByWeekId({
    queryKey: 'doris-units-sold',
    metricType: DorisMetricTypeEnum.UnitsSold,
    methodName: DorisMetricEndPointEnum.SalesEndPoint,
    resultCount: 120
  });

  const { response: totalTrafficDorisResponse, isLoading: loadingTotalTraffic } = useDorisMetricByWeekId({
    queryKey: 'doris-total-traffic',
    metricType: DORIS_API_METRIC_TYPE.TOTAL_TRAFFIC,
    resultCount: 120
  });

  // Transform units sold data
  const dorisUnitsSoldPrimaryData = dorisUnitsSoldResponse?.primary?.records;
  const dorisUnitsSoldComparisonData = dorisUnitsSoldResponse?.comparison?.records;

  const unitsSoldPrimaryData = transformUnitsSoldData(dorisUnitsSoldPrimaryData);
  const unitsSoldComparisonData = transformUnitsSoldData(dorisUnitsSoldComparisonData);

  // Transform traffic data
  const { primary: trafficPrimaryResponse, comparison: trafficComparisonResponse } = totalTrafficDorisResponse;
  const trafficPrimaryData = transformTrafficData(trafficPrimaryResponse?.records);
  const trafficComparisonData = transformTrafficData(trafficComparisonResponse?.records);

  const conversionRatePrimaryData = calculateDerivedMetricByWeekId({
    dataSet1: unitsSoldPrimaryData,
    dataSet2: trafficPrimaryData
  });
  const conversionRateComparisonData = calculateDerivedMetricByWeekId({
    dataSet1: unitsSoldComparisonData,
    dataSet2: trafficComparisonData
  });

  const {
    primarySeries,
    secondarySeries,
    primaryDerivedMetricAverage,
    comparisonDerivedMetricAverage,
    primaryDerivedMetricPercentChange,
    primaryLastWeekValue,
    comparisonLastWeekValue,
    primaryPercentChangeLastWeek
  } = useChartMetricByWeekId({
    primaryRawData: conversionRatePrimaryData,
    secondaryRawData: conversionRateComparisonData,
    primaryDerivedMetricRawData: {
      dataSource1: unitsSoldPrimaryData,
      dataSource2: trafficPrimaryData
    },
    secondaryDerivedMetricRawData: {
      dataSource1: unitsSoldComparisonData,
      dataSource2: trafficComparisonData
    }
  });

  return {
    loading: loadingDorisUnitsSold || loadingTotalTraffic,
    primarySeries,
    secondarySeries,
    primaryDerivedMetricAverage: weekRange === WeekRange.LAST_WEEK ? primaryLastWeekValue : primaryDerivedMetricAverage,
    comparisonDerivedMetricAverage:
      weekRange === WeekRange.LAST_WEEK ? comparisonLastWeekValue : comparisonDerivedMetricAverage,
    primaryDerivedMetricPercentChange:
      weekRange === WeekRange.LAST_WEEK ? primaryPercentChangeLastWeek : primaryDerivedMetricPercentChange
  };
};

/**
 * This component will display a spine chart of the conversion rate by week
 * It will use a combination of elastic search of unitsSold and doris traffic
 *
 */
const AtlasV2ConversionSplineV2Chart = () => {
  const { displayName: mainPeriodDisplayName } = useAppSelector((state) => state.mainTimePeriod);
  const { displayName: comparisonPeriodDisplayName } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );

  const {
    primarySeries: primaryData,
    secondarySeries: secondaryData,
    loading,
    primaryDerivedMetricAverage,
    comparisonDerivedMetricAverage,
    primaryDerivedMetricPercentChange
  } = useConversionV2ChartData();

  // Convert to SplineChartV2 format
  const primarySeries = useMemo(
    () => convertToV2Series(primaryData, 'primary', mainPeriodDisplayName),
    [primaryData, mainPeriodDisplayName]
  );

  const comparisonSeries = useMemo(
    () => convertToV2Series(secondaryData, 'comparison', comparisonPeriodDisplayName),
    [secondaryData, comparisonPeriodDisplayName]
  );

  const exportSplineChartToCsv = useMemo(
    () => async () => {
      try {
        const csvContent = convertSplineChartV2SeriesToDelimitedData(
          primarySeries,
          comparisonSeries,
          mainPeriodDisplayName,
          comparisonPeriodDisplayName
        );

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
        saveAs(blob, `Conversion-rate.csv`);
      } catch (e) {
        console.error('Error exporting spline chart to CSV:', e);
      }
    },
    [mainPeriodDisplayName, primarySeries, comparisonPeriodDisplayName, comparisonSeries]
  );

  const getBeaconChartWithLegendProps = () => {
    const defaultProps: Omit<BeaconChartWithLegendProps, 'children'> = {
      title: 'Conversion Rate',
      primaryLegendProps: {
        displayName: mainPeriodDisplayName,
        metric: formatMetric(primaryDerivedMetricAverage, METRICTYPE.PERCENT, {
          showFullValue: true,
          decimalPlaces: 2
        }),
        change: formatMetric(Math.abs(primaryDerivedMetricPercentChange), METRICTYPE.PERCENT, {
          showFullValue: true,
          decimalPlaces: 2
        }),
        direction: getDirection(primaryDerivedMetricPercentChange)
      },
      comparisonLegendProps: {
        displayName: comparisonPeriodDisplayName,
        metric: formatMetric(comparisonDerivedMetricAverage, METRICTYPE.PERCENT, {
          showFullValue: true,
          decimalPlaces: 2
        })
      },
      loading,
      loadingComponent: <SplineChartLoading />,
      showTitleSummary: true,
      convertSeriesToDelimitedData: exportSplineChartToCsv
    };

    return defaultProps;
  };

  return (
    <BeaconChartWithLegend {...getBeaconChartWithLegendProps()}>
      <SplineChartV2
        primarySeries={primarySeries}
        comparisonSeries={comparisonSeries}
        metricType={METRICTYPE.PERCENT}
        NoDataPlaceholder={
          <NoDataPlaceHolder
            title="Data currently unavailable"
            description="There is currently no data matching your selected criteria. Please try another query to view insights."
            flexProps={{ margin: '80px auto' }}
          />
        }
      />
    </BeaconChartWithLegend>
  );
};

export default AtlasV2ConversionSplineV2Chart;
